import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

export interface App {
  id: string;
  app_name: string;
  app_key: string;
  app_description: string;
  app_image: string;
  is_active: boolean;
  is_public: boolean;
  version: string;
  category: string;
  tags: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppsResponse {
  apps: App[];
  total: number;
  page: number;
  size: number;
  pages: number;
}


// User metadata returned by app-accounts API (used for "Connected by" display)
export interface AppAccountUserMetadata {
  user_id?: string;
  name?: string;
  email_id?: string;
  webhook_url?: string | null;
}

// New interface for connected app accounts
export interface ConnectedAppAccount {
  app_id: string;
  channel_id: string;
  connection_name: string;
  connection_status: string;
  connection_settings: any;
  webhook_url: string;
  callback_url: string;
  is_active: boolean;
  auto_sync: boolean;
  sync_interval: number;
  id: string;
  connected_by: string;
  user_metadata?: AppAccountUserMetadata | null;
  last_sync_at: string;
  sync_status: string;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface ConnectedAppsResponse {
  app_accounts: ConnectedAppAccount[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Enhanced interface that includes connection status
export interface AppWithConnectionStatus extends App {
  isConnected: boolean;
  connectionDetails?: ConnectedAppAccount;
}

export interface EnhancedAppsResponse {
  apps: AppWithConnectionStatus[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface CategoriesResponse {
  categories: string[];
  total: number;
}

class AppsService {
  private baseUrl = API_CONFIG.API_BASE_URL;

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'accept': 'application/json'
    };
  }


  async getApps(page: number = 1, size: number = 10, channelId?: string, category?: string, subCategory?: string, filterIsActive?: boolean): Promise<EnhancedAppsResponse> {
    try {
      const headers = this.getAuthHeaders();
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
      });
      
      if (category) {
        params.append('category', category);
      }
      
      if (subCategory) {
        params.append('sub_category', subCategory);
      }
      
      if (filterIsActive !== undefined) {
        params.append('is_active', filterIsActive.toString());
      }
      
      // Fetch all apps from /api/v1/apps/
      const appsResponse = await fetch(`${this.baseUrl}${API_ENDPOINTS.APPS.LIST}?${params.toString()}`, {
        method: 'GET',
        headers
      });

      if (!appsResponse.ok) {
        throw new Error(`HTTP error! status: ${appsResponse.status}`);
      }

      const appsData: AppsResponse = await appsResponse.json();
      
      // If channelId is provided, also fetch connected apps from /api/v1/app-accounts/
      let connectedApps: ConnectedAppAccount[] = [];
      if (channelId) {
        try {
          const connectedResponse = await fetch(`${this.baseUrl}${API_ENDPOINTS.APP_ACCOUNTS.LIST}?channel_id=${channelId}`, {
            method: 'GET',
            headers
          });

          if (connectedResponse.ok) {
            const connectedData: ConnectedAppsResponse = await connectedResponse.json();
            connectedApps = connectedData.app_accounts || [];
          }
        } catch (error) {
          console.warn('Failed to fetch connected apps:', error);
          // Continue with apps data even if connected apps fetch fails
        }
      }

      // Create a map of connected apps for quick lookup
      const connectedAppsMap = new Map<string, ConnectedAppAccount>();
      connectedApps.forEach(connectedApp => {
        connectedAppsMap.set(connectedApp.app_id, connectedApp);
      });

      // Enhance apps with connection status
      const enhancedApps: AppWithConnectionStatus[] = (appsData.apps || []).map(app => {
        const connectionDetails = connectedAppsMap.get(app.id);
        return {
          ...app,
          isConnected: !!connectionDetails,
          connectionDetails
        };
      });

      return {
        apps: enhancedApps,
        total: appsData.total || 0,
        page: appsData.page || page,
        size: appsData.size || size,
        pages: appsData.pages || 1
      };
    } catch (error) {
      console.error('Error fetching apps:', error);
      throw error;
    }
  }


  async searchApps(query: string, page: number = 1, size: number = 10, channelId?: string, category?: string, subCategory?: string, filterIsActive?: boolean): Promise<EnhancedAppsResponse> {
    try {
      const headers = this.getAuthHeaders();
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        search: query
      });
      
      if (category) {
        params.append('category', category);
      }
      
      if (subCategory) {
        params.append('sub_category', subCategory);
      }
      
      if (filterIsActive !== undefined) {
        params.append('is_active', filterIsActive.toString());
      }
      
      // Fetch searched apps from /api/v1/apps/
      const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.APPS.LIST}?${params.toString()}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const appsData: AppsResponse = await response.json();
      
      // If channelId is provided, also fetch connected apps from /api/v1/app-accounts/
      let connectedApps: ConnectedAppAccount[] = [];
      if (channelId) {
        try {
          const connectedResponse = await fetch(`${this.baseUrl}${API_ENDPOINTS.APP_ACCOUNTS.LIST}?channel_id=${channelId}`, {
            method: 'GET',
            headers
          });

          if (connectedResponse.ok) {
            const connectedData: ConnectedAppsResponse = await connectedResponse.json();
            connectedApps = connectedData.app_accounts || [];
          }
        } catch (error) {
          console.warn('Failed to fetch connected apps during search:', error);
        }
      }

      // Create a map of connected apps for quick lookup
      const connectedAppsMap = new Map<string, ConnectedAppAccount>();
      connectedApps.forEach(connectedApp => {
        connectedAppsMap.set(connectedApp.app_id, connectedApp);
      });

      // Enhance apps with connection status
      const enhancedApps: AppWithConnectionStatus[] = (appsData.apps || []).map(app => {
        const connectionDetails = connectedAppsMap.get(app.id);
        return {
          ...app,
          isConnected: !!connectionDetails,
          connectionDetails
        };
      });

      return {
        apps: enhancedApps,
        total: appsData.total || 0,
        page: appsData.page || page,
        size: appsData.size || size,
        pages: appsData.pages || 1
      };
    } catch (error) {
      console.error('Error searching apps:', error);
      throw error;
    }
  }

  async getAppById(appId: string): Promise<App> {
    try {
      const headers = this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.APPS.GET_BY_ID(appId)}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching app:', error);
      throw error;
    }
  }

  async getCategories(): Promise<CategoriesResponse> {
    try {
      const headers = this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.APPS.CATEGORIES}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: CategoriesResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Fetches app-accounts for a channel only. Use after connection success to update
   * connection status without refetching the full apps catalog.
   */
  async getAppAccountsForChannel(channelId: string): Promise<ConnectedAppAccount[]> {
    const headers = this.getAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}${API_ENDPOINTS.APP_ACCOUNTS.LIST}?channel_id=${channelId}`,
      { method: 'GET', headers }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: ConnectedAppsResponse = await response.json();
    return data.app_accounts || [];
  }

  // New method to disconnect an app
  async disconnectApp(connectionId: string): Promise<void> {
    try {
      const headers = this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${API_ENDPOINTS.APP_ACCOUNTS.DELETE(connectionId)}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error disconnecting app:', error);
      throw error;
    }
  }
}

export const appsService = new AppsService();
